// Financial Reporting tab — ONE drawer per request, carrying its whole life.
//
// It replaces three surfaces that each owned a third of the same object: an
// issue modal, a per-item review drawer opened on top of the request list, and
// a report drawer opened from the same row. A reviewer working one request had
// to hold in their head which of those they were in, and the items were never
// visible beside the report written from them.
//
// The three stages are the three things that happen to a request:
//   ① Request — what was asked for, of whom, by when.
//   ② Review  — what came back, item by item.
//   ③ Report  — the summary written from it, and where it was filed.
//
// Only the CURRENT stage is expanded. The ones behind it collapse to a single
// line of what they settled and reopen on click, because a finished stage is
// context, not work — and the review stage on a nineteen-item request is tall
// enough to push the report off the screen entirely.
//
// 🔴 The collapsibles are `CollapsibleSection` (a button + aria-expanded), NOT
// `defaults/AccordionCard`, which is `<details>/<summary>` — the shape behind
// the tab-freeze incident, where a Modal mounted inside a closed `<details>`
// painted nothing while still locking the page's scroll.

import { useState } from "react";
import {
  Button,
  StatusBadge,
  StatusStepper,
  SubTable,
  Surface,
  type StatusTone,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { CollapsibleSection } from "@/components/counterparties/CollapsibleSection";
import { formatCalendarDate, formatDateInTz } from "@/design-system/lib/format";
import {
  REPORT_REQUEST_ITEM_KIND_LABEL,
  REPORT_REQUEST_ITEM_STATUS_LABEL,
  REPORT_REQUEST_STATUS_LABEL,
  TERMINAL_REQUEST_STATUSES,
  type ReportRequestDto,
  type ReportRequestItemDto,
} from "@/api/portfolioAdmin/reportRequestsApi";
import { isReportRequestOverdue } from "../defaults/timeline";
import { ReportRequestItemReviewPanel } from "./ReportRequestItemReviewPanel";
import { ReportRequestReportStage } from "./ReportRequestReportStage";
import { hasSubmission, lifecycleStage } from "./reportRequestSummary";

interface ReportRequestLifecycleDrawerProps {
  loanId: string;
  /** Null when closed. Re-derived live by the caller, never captured. */
  request: ReportRequestDto | null;
  /** Reporting.ReviewRequests — gates every write in every stage. */
  canReview: boolean;
  tz?: string | null;
  onClose: () => void;
  onCancelRequest: (request: ReportRequestDto) => void;
  onCompleteWithExceptions: (request: ReportRequestDto) => void;
}

const STEPS = [
  {
    key: "request",
    label: "Request",
    hint: "What was asked for, of whom, and by when.",
  },
  {
    key: "review",
    label: "Review",
    hint: "Approve or return each item the borrower sent back.",
  },
  {
    key: "report",
    label: "Report",
    hint: "Write the summary and file it into the loan's documents.",
  },
];

/**
 * 🔴 An item's badge is derived from the STAGED SUBMISSION, not from
 * `item.status`.
 *
 * `ReportRequestItemStatuses.Submitted` and `UnderReview` have no production
 * writer, so an item the borrower HAS submitted still reads `pending` — and a
 * badge printed straight off `status` says "Not yet provided" over a document
 * sitting in the panel below it. `approved` and `returned` ARE written, so they
 * are trusted; everything else is answered by whether a submission exists and
 * whether it is still awaiting review.
 */
function itemBadge(item: ReportRequestItemDto): { label: string; tone: StatusTone } {
  if (item.status === "approved") {
    return { label: REPORT_REQUEST_ITEM_STATUS_LABEL.approved, tone: "ok" };
  }
  if (item.awaitingReview) return { label: "Waiting for your review", tone: "info" };
  if (item.status === "returned") {
    return { label: REPORT_REQUEST_ITEM_STATUS_LABEL.returned, tone: "warn" };
  }
  if (hasSubmission(item)) return { label: "Reviewed", tone: "neutral" };
  return { label: REPORT_REQUEST_ITEM_STATUS_LABEL.pending, tone: "neutral" };
}

function requestTone(status: string): StatusTone {
  switch (status) {
    case "complete":
    case "reported":
      return "ok";
    case "under-review":
      return "progress";
    case "in-progress":
      return "info";
    case "cancelled":
      return "muted";
    default:
      return "neutral";
  }
}

export function ReportRequestLifecycleDrawer({
  loanId,
  request,
  canReview,
  tz,
  onClose,
  onCancelRequest,
  onCompleteWithExceptions,
}: ReportRequestLifecycleDrawerProps) {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  if (!request) return null;

  const { index: stageIndex, complete } = lifecycleStage(request);
  // Each section is keyed on the request AND its stage, so `defaultOpen` is
  // re-seeded when the request advances under the reader — approving the last
  // item opens the report stage rather than leaving them looking at a finished
  // list. After that the sections are theirs: opening a settled stage back up
  // is a click, and it stays open until the request itself moves on.
  const sectionKey = `${request.id}-${stageIndex}`;
  const active = STEPS[stageIndex].key;
  const terminal = TERMINAL_REQUEST_STATUSES.includes(request.status);
  const overdue = isReportRequestOverdue(request);

  const canCompleteWithExceptions =
    canReview &&
    !terminal &&
    !request.completeWithExceptions &&
    request.outstandingCount > 0;

  const columns: SubTableColumn<ReportRequestItemDto>[] = [
    {
      key: "name",
      header: "Item",
      render: (item) => (
        <span className="min-w-0">
          <span className="block text-small text-onyx">{item.displayName}</span>
          <span className="block text-caption text-gray-mid">
            {REPORT_REQUEST_ITEM_KIND_LABEL[item.kind] ?? item.kind}
            {item.guarantorLabel ? ` · ${item.guarantorLabel}` : ""}
          </span>
        </span>
      ),
      sortAccessor: (item) => item.displayName,
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (item) =>
        item.submittedAt ? (
          <span className="text-caption text-gray-mid">
            {formatDateInTz(item.submittedAt, tz)}
          </span>
        ) : (
          // Not a pill: nothing has happened, and a state absent is muted
          // prose, never a badge competing with the ones that mean something.
          <span className="text-caption text-gray-mid">—</span>
        ),
      sortAccessor: (item) => item.submittedAt ?? "",
    },
    {
      key: "status",
      header: "Status",
      align: "right",
      render: (item) => {
        const badge = itemBadge(item);
        return <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>;
      },
      searchText: (item) => itemBadge(item).label,
    },
  ];

  return (
    <ResourceDrawer
      open
      width="xl"
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${request.requestTypeLabel} · ${request.periodLabel}`}
      subtitle={`Due ${formatCalendarDate(request.dueDate)}${overdue ? " · Overdue" : ""}`}
      testId="report-request-lifecycle-drawer"
      headerActions={
        <div className="flex items-center gap-space-2">
          <StatusBadge tone={requestTone(request.status)}>
            {REPORT_REQUEST_STATUS_LABEL[request.status] ?? request.status}
          </StatusBadge>
          {request.completeWithExceptions && (
            <StatusBadge tone="warn">With exceptions</StatusBadge>
          )}
        </div>
      }
    >
      {/* Declared rather than probed: the DOM probe cannot run before the first
          paint, so a sub-table in a drawer would render its page-level onyx
          header for one frame — and never resolve at all in jsdom. */}
      <Surface kind="drawer">
        <div className="flex flex-col gap-space-4">
          <StatusStepper
            steps={STEPS}
            currentIndex={stageIndex}
            complete={complete}
            label="Request lifecycle"
            data-testid="report-request-stepper"
          />

          {/* ① Request */}
          <CollapsibleSection
            key={`${sectionKey}-request`}
            title="① Request"
            defaultOpen={active === "request"}
            warning={
              <span data-testid="stage-request-summary">
                {request.periodLabel} · Due {formatCalendarDate(request.dueDate)} ·{" "}
                {request.itemCount} item{request.itemCount === 1 ? "" : "s"} asked for
                {request.issuedByUserName ? ` by ${request.issuedByUserName}` : ""}
              </span>
            }
          >
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
              <div>
                <dt className="text-caption text-gray-mid">Reporting period</dt>
                <dd className="text-small text-onyx m-0">
                  {request.periodLabel}
                  <span className="block text-caption text-gray-mid">
                    {formatCalendarDate(request.periodStart)} –{" "}
                    {formatCalendarDate(request.periodEnd)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-caption text-gray-mid">Due</dt>
                <dd
                  className={
                    overdue
                      ? "text-small font-semibold text-status-error m-0"
                      : "text-small text-onyx m-0"
                  }
                >
                  {formatCalendarDate(request.dueDate)}
                  {overdue ? " · Overdue" : ""}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-gray-mid">Issued</dt>
                <dd className="text-small text-onyx m-0">
                  {formatDateInTz(request.issuedAt, tz)}
                  {request.issuedByUserName ? ` · ${request.issuedByUserName}` : ""}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-gray-mid">Progress</dt>
                <dd className="text-small text-onyx m-0">
                  {request.approvedCount}/{request.itemCount} approved
                  {request.outstandingCount > 0
                    ? ` · ${request.outstandingCount} outstanding`
                    : ""}
                </dd>
              </div>
            </dl>

            {request.completeWithExceptions && request.exceptionsNote && (
              <p className="text-caption text-gray-mid m-0 mt-space-3">
                Exceptions: {request.exceptionsNote}
              </p>
            )}

            {canReview && !terminal && (
              <div className="mt-space-3 flex flex-wrap items-center justify-end gap-space-3">
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => onCancelRequest(request)}
                  data-testid={`report-request-cancel-${request.id}`}
                >
                  Cancel request
                </Button>
              </div>
            )}
          </CollapsibleSection>

          {/* ② Review */}
          <CollapsibleSection
            key={`${sectionKey}-review`}
            title="② Review"
            defaultOpen={active === "review"}
            warning={
              <span data-testid="stage-review-summary">
                {request.approvedCount} of {request.itemCount} approved
                {request.outstandingCount > 0
                  ? ` · ${request.outstandingCount} still outstanding`
                  : " · nothing outstanding"}
              </span>
            }
          >
            <div className="flex flex-col gap-space-3">
              <SubTable
                columns={columns}
                rows={request.items}
                rowKey={(item) => item.id}
                caption={`Items on ${request.periodLabel}`}
                rowTestId={(item) => `report-request-item-${item.id}`}
                data-testid={`report-request-items-${request.id}`}
                empty={{ message: "This request asks for nothing." }}
                // Whole-row click, and `SubTable` supplies the keyboard half
                // (role, tab stop, Enter/Space) that a bare <tr> cannot have.
                onRowClick={(item) =>
                  setExpandedItemId((prev) => (prev === item.id ? null : item.id))
                }
                rowSelected={(item) => item.id === expandedItemId}
                renderExpanded={(item) =>
                  item.id === expandedItemId ? (
                    <ReportRequestItemReviewPanel
                      loanId={loanId}
                      item={item}
                      request={request}
                      canReview={canReview}
                      tz={tz}
                    />
                  ) : null
                }
              />

              {canCompleteWithExceptions && (
                <div className="flex flex-wrap items-center justify-end gap-space-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onCompleteWithExceptions(request)}
                    data-testid={`report-request-exceptions-${request.id}`}
                  >
                    Complete with exceptions
                  </Button>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* ③ Report */}
          <CollapsibleSection
            key={`${sectionKey}-report`}
            title="③ Report"
            defaultOpen={active === "report"}
            warning={
              <span data-testid="stage-report-summary">
                {request.filedDocumentId !== null
                  ? `Filed${request.filedAt ? ` ${formatDateInTz(request.filedAt, tz)}` : ""}`
                  : request.generatedReportId !== null
                    ? "Generated — not filed yet"
                    : request.canGenerateReport
                      ? "Ready to write"
                      : "Waiting on the items above"}
              </span>
            }
          >
            <ReportRequestReportStage
              loanId={loanId}
              request={request}
              canReview={canReview}
              tz={tz}
              onFiled={onClose}
            />
          </CollapsibleSection>
        </div>
      </Surface>
    </ResourceDrawer>
  );
}
